import { Banner } from "components/DesignSystem/Banner/Banner";
import { Info } from "components/icons/Info";
import NumberFlow from "@number-flow/react";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { ProjectedTaxImpact as ProjectedTaxImpactType } from "store/apis/financialClosing";
import { currency } from "utils/Currency";

type Props = {
  data: ProjectedTaxImpactType | null | undefined;
  entityId: string;
  closingDate?: string | null;
};

const isZero = (value: string) => Number(value) === 0;

const NetIncomeCard = ({
  adjustedNetIncome,
  closingDate,
}: {
  adjustedNetIncome: string;
  closingDate?: string | null;
}) => {
  const colorCls =
    Number(adjustedNetIncome) < 0 ? "t-text-red" : "t-text-dark_green-70";

  return (
    <div className="t-rounded-lg t-p-4 sm:t-px-3 t-flex t-flex-col t-gap-2 t-border t-border-solid t-border-neutral-10">
      <span className="t-text-body-sm t-font-semibold t-text-text-60">
        Current Profit/Loss
      </span>

      <div className="t-flex t-flex-col t-gap-0.5">
        <span className={`t-text-subtitle-sm t-font-bold ${colorCls}`}>
          <NumberFlow
            value={Math.round(Math.abs(Number(adjustedNetIncome)))}
            format={{
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0,
            }}
            locales="en-US"
            willChange
          />
        </span>
        {closingDate && (
          <span className="t-text-caption t-text-text-30 t-gap-1">
            Based on {dayjs(closingDate).format("MMM 'YY")} data
          </span>
        )}
      </div>
    </div>
  );
};

export const ProjectedTaxImpact = ({ data, entityId, closingDate }: Props) => {
  const hasData = !!data && !isZero(data.non_us_rd_expense);

  const taxBannerDescription = data
    ? `Estimated federal tax liability: ${currency({
        amount: Math.round(Math.abs(Number(data.estimated_tax_liability))),
        maximumFractionDigits: 0,
      })} at 21% rate for this period.`
    : "";

  return (
    <div className="t-flex t-flex-col t-gap-2.5 t-border t-border-solid t-border-neutral-10 t-rounded-lg t-p-4 sm:t-px-3">
      <NetIncomeCard
        adjustedNetIncome={data?.adjusted_net_income ?? "0"}
        closingDate={closingDate}
      />

      {!hasData && data && (
        <>
          <div className="t-flex t-items-center t-justify-between">
            <span className="t-text-body-sm t-text-text-30">
              Non-US R&D Expense
            </span>
            <span className="t-text-body-sm t-font-medium t-text-text-60">
              {currency({
                amount: Math.round(Math.abs(Number(data.non_us_rd_expense))),
                maximumFractionDigits: 0,
              })}
            </span>
          </div>
          <Banner
            type="info"
            description={
              <span>
                {taxBannerDescription}{" "}
                <Link
                  to={`/tax/advance-tax?entity=${entityId}`}
                  className="t-text-purple t-font-medium t-text-nowrap"
                  style={{ textDecoration: "none" }}
                >
                  Know more
                </Link>
              </span>
            }
          />
        </>
      )}

      {hasData && data && (
        <>
          <div className="t-flex t-items-center t-justify-between">
            <span className="t-text-body-sm t-text-text-30">
              Current Year Amortized Amount
            </span>
            <span className="t-text-body-sm t-font-medium t-text-text-60">
              {currency({
                amount: Math.round(Math.abs(Number(data.allowed_deduction))),
                maximumFractionDigits: 0,
              })}
            </span>
          </div>
          <div className="t-flex t-items-center t-justify-between">
            <span className="t-text-body-sm t-text-text-30">
              Amortized for next 15 years
            </span>
            <span className="t-text-body-sm t-font-medium t-text-text-60">
              {currency({
                amount: Math.round(
                  Math.abs(
                    Number(data.non_us_rd_expense) -
                      Number(data.allowed_deduction)
                  )
                ),
                maximumFractionDigits: 0,
              })}
            </span>
          </div>
          <div className="t-flex t-items-center t-justify-between">
            <span className="t-text-body-sm t-text-text-30">
              Non-US R&D Expense
            </span>
            <span className="t-text-body-sm t-font-medium t-text-text-60">
              {currency({
                amount: Math.round(Math.abs(Number(data.non_us_rd_expense))),
                maximumFractionDigits: 0,
              })}
            </span>
          </div>
          <Banner
            type="info"
            description={
              <span>
                {taxBannerDescription}{" "}
                <Link
                  to={`/tax/advance-tax?entity=${entityId}`}
                  className="t-text-purple t-font-medium t-text-nowrap"
                  style={{ textDecoration: "none" }}
                >
                  Know more
                </Link>
              </span>
            }
          />
          <p className="t-text-body-sm t-text-text-30 t-italic">
            <strong className="t-not-italic">Note:</strong> Non-US R&D expenses
            are amortized over 15 years instead of being deducted immediately.
            Only a portion is deductible this year.
          </p>
        </>
      )}
    </div>
  );
};
